import { QueryClient } from '@tanstack/react-query';
import { isAppError } from '../api/errors';

/**
 * TanStack Query defaults (docs/07-implementation-roadmap.md F0.3).
 *
 * The only client state this app has is the auth session and UI ephemera —
 * everything else is server state, which is why there is no Redux here.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // One retry, and never on a 4xx. Retrying a 403 asks the server the
        // same question three times and gets the same answer; retrying a 422
        // re-submits a form the server has already judged. Only transport
        // failures and 5xx are worth a second attempt.
        //
        // **A 429 is the 4xx that looks like an exception and is not** (F7.5).
        // It is retryable by a person and must not be retried by the client:
        // the window is up to a minute long, so a retry a second later is
        // refused for the same reason — and it spends one more request from a
        // budget that is 100 per minute for the entire office. Automatic
        // retries are how a busy minute becomes a stuck one. `isAutoRetryable`
        // is that narrower question; `isRetryable` still governs whether a
        // screen offers the person a button.
        retry: (failureCount, error) => {
          if (failureCount >= 1) return false;
          return isAppError(error) ? error.isAutoRetryable : true;
        },
        // Staff move between the list and a record and back constantly. 30s is
        // long enough that navigation feels instant and short enough that a
        // colleague's edit shows up without a manual refresh.
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
      mutations: {
        // A write is never retried automatically. The API has no idempotency
        // key on these routes yet (docs/04 §8 offers one only for mobile), so a
        // silent retry of a status change is a second history row.
        retry: false,
      },
    },
  });
}
